import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, X } from "lucide-react";
import { useEnquiry } from "@/context/EnquiryContext";
import { ZONES, zoneName } from "@/data/site";
import { MaskLines, Reveal } from "@/components/site/Motion";
import { T } from "@/context/ContentContext";
import { useSeo } from "@/hooks/useSeo";
import { TripCard } from "@/components/site/TripCard";
import { useTrips } from "@/hooks/useTrips";

const REGIONS = [["", "All"], ["india", "India"], ["international", "International"]];
const CATS = [["", "Any style"], ["group", "Group"], ["georgia", "Georgia"], ["honeymoon", "Honeymoon"], ["weekend", "Weekend"], ["festival", "Festive"]];
const SORTS = [["featured", "Featured"], ["price-asc", "Price: low to high"], ["price-desc", "Price: high to low"], ["days", "Shortest first"]];

const chip = (on) =>
  `rounded-full px-4 py-2 text-sm font-medium border transition-colors duration-300 ${on ? "bg-ink text-cream border-ink" : "border-ink/15 text-ink/75 hover:border-ink/40"}`;

export default function Trips() {
  const { trips, loading } = useTrips();
  const { openEnquiry } = useEnquiry();
  const [params, setParams] = useSearchParams();
  const f = {
    q: params.get("q") || "", region: params.get("region") || "", category: params.get("category") || "",
    destination: params.get("destination") || "", sort: params.get("sort") || "featured", zone: params.get("zone") || "",
  };
  const zone = zoneName(f.zone);
  const update = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next, { replace: true });
  };
  const destinations = useMemo(() => [...new Set(trips.map((t) => t.destination))], [trips]);

  const list = useMemo(() => {
    const q = f.q.toLowerCase();
    const out = trips.filter((t) =>
      (!f.region || t.region === f.region) &&
      (!f.zone || t.zone === f.zone) &&
      (!f.category || (t.categories || []).includes(f.category)) &&
      (!f.destination || t.destination.toLowerCase() === f.destination.toLowerCase()) &&
      (!q || `${t.title} ${t.destination}`.toLowerCase().includes(q)));
    if (f.sort === "price-asc") out.sort((a, b) => a.price - b.price);
    if (f.sort === "price-desc") out.sort((a, b) => b.price - a.price);
    if (f.sort === "days") out.sort((a, b) => a.days - b.days);
    return out;
  }, [trips, f.q, f.region, f.category, f.destination, f.sort, f.zone]);

  const active = f.q || f.region || f.category || f.destination || f.zone;
  const heading = zone || f.destination;
  useSeo({
    title: heading ? `${heading} Tour Packages from Delhi` : "Tour Packages from Delhi: Georgia, India & International Trips",
    description: `Browse ${heading ? `${heading} ` : ""}group trips, honeymoons and custom tour packages by Trippy Tastic, a Delhi travel agency and Georgia specialist.`,
    path: "/trips",
  });

  return (
    <main className="bg-paper min-h-screen pt-32 sm:pt-40 pb-24" data-testid="trips-page">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <p className="eyebrow text-terra mb-4">{zone ? <T k="trips.zone-eyebrow">Explore by region</T> : <T k="trips.eyebrow">All trips</T>}</p>
        <h1 key={heading} className="font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold tracking-[-0.04em] leading-[0.92]" data-testid="trips-heading">
          <MaskLines lines={heading
            ? [`${heading},`, <T k="trips.zone-title" className="font-serif italic font-normal">your way.</T>]
            : [<T k="trips.title.0">Find your</T>, <T k="trips.title.1" className="font-serif italic font-normal">next escape.</T>]} />
        </h1>

        <Reveal className="mt-12 space-y-5 border-y border-ink/10 py-6" data-testid="trip-filters">
          <div className="flex flex-col md:flex-row gap-4 md:items-center">
            <label className="flex items-center gap-3 flex-1 rounded-full border border-ink/15 px-5 py-3 focus-within:border-terra transition-colors">
              <Search className="h-4 w-4 text-stone-600" />
              <input value={f.q} onChange={(e) => update("q", e.target.value)} placeholder="Search trips or places" data-testid="trips-search-input"
                className="w-full bg-transparent outline-none placeholder:text-stone-600" />
            </label>
            <select value={f.zone} onChange={(e) => update("zone", e.target.value)} data-testid="trips-zone-select"
              className="rounded-full border border-ink/15 px-5 py-3 bg-paper outline-none cursor-pointer">
              <option value="">All regions</option>
              {ZONES.map((z) => <option key={z.slug} value={z.slug}>{z.name}</option>)}
            </select>
            <select value={f.destination} onChange={(e) => update("destination", e.target.value)} data-testid="trips-destination-select"
              className="rounded-full border border-ink/15 px-5 py-3 bg-paper outline-none cursor-pointer">
              <option value="">All destinations</option>
              {destinations.map((d) => <option key={d}>{d}</option>)}
            </select>
            <select value={f.sort} onChange={(e) => update("sort", e.target.value)} data-testid="trips-sort-select"
              className="rounded-full border border-ink/15 px-5 py-3 bg-paper outline-none cursor-pointer">
              {SORTS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
          <div className="flex flex-wrap gap-2">
            {REGIONS.map(([k, l]) => <button key={l} onClick={() => update("region", k)} className={chip(f.region === k)} data-testid={`trips-region-${k || "all"}`}>{l}</button>)}
            <span className="w-px bg-ink/10 mx-1" />
            {CATS.map(([k, l]) => <button key={l} onClick={() => update("category", k)} className={chip(f.category === k)} data-testid={`trips-category-${k || "any"}`}>{l}</button>)}
            {active && (
              <button onClick={() => setParams({}, { replace: true })} data-testid="trips-reset-btn" className="inline-flex items-center gap-1 px-3 text-sm text-terra font-semibold">
                <X className="h-4 w-4" /> Reset
              </button>
            )}
          </div>
        </Reveal>

        <p className="eyebrow text-stone-600 mt-8" data-testid="trips-count">{loading ? "Loading…" : `${list.length} trip${list.length === 1 ? "" : "s"}`}</p>
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
          {list.map((t, i) => <Reveal key={t.id} delay={(i % 3) * 0.08}><TripCard trip={t} /></Reveal>)}
        </div>
        {!loading && list.length === 0 && (
          <div className="mt-10 rounded-[28px] bg-cream p-8 sm:p-12 max-w-3xl" data-testid="trips-empty">
            <p className="font-serif italic text-3xl sm:text-4xl">{zone ? `${zone} trips are planned on request.` : "Nothing here yet. Try a different filter."}</p>
            {zone && (
              <>
                <p className="mt-3 text-stone-600">Tell us your dates and group size and we'll build a custom itinerary for you.</p>
                <div className="mt-6 flex flex-wrap gap-4 items-center">
                  <button onClick={() => openEnquiry({ trip_title: `Custom trip: ${zone}` })} data-testid="trips-empty-enquire-btn"
                    className="rounded-full bg-terra text-paper px-6 py-3 font-semibold hover:bg-[#a5370a] transition-colors">Plan a custom {zone} trip</button>
                  <Link to="/trips" className="underline underline-offset-4">See all trips</Link>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </main>
  );
}
